import * as React from "react";
import { toast } from "@grandvisionhq/common";
import { useFeatureToggles, useIntl } from "@grandvisionhq/state";
import { useSetCustomerDetails } from "../../../../hooks/operations/hooks/operations/use-set-customer-details.js";
import { isFormDataValid } from "../../../utils/LoginExpressCheckout/utils/validate-form-data.js";
import { useLoggedInCheckout } from "../../../context/LoginExpressCheckout/context/context.js";
import { ShippingMethod } from "../../../../models/step-shipping/models/step-shipping/shipping-methods.js";
import { getCustomerDetailsPayload } from "../../../../utils/utils/get-customer-details-payload.js";
import { useCheckoutSettings } from "../../../../context/CheckoutSettings/context/CheckoutSettings/context.js";
const useValidateAndSetOrderDetails = (props) => {
  const { formFieldsMap, features, serviceStore, consent: consentSettings } = useCheckoutSettings();
  const isServiceStoreEnabled = features?.isServiceStoreEnabled;
  const { isMultilingualEnabled } = useFeatureToggles();
  const intl = useIntl();
  const {
    userDetailsState,
    shippingState,
    isCompanyOrder,
    setisOrderDetailsValid,
    setIsOrderInProgress,
    updateConsents,
    isAddressesEqual
  } = useLoggedInCheckout();
  const { pickupPoint, store } = React.useMemo(() => shippingState, [shippingState]);
  const isServiceStoreValid = React.useCallback(
    (selectedShippingMethod) => Boolean(selectedShippingMethod.method == ShippingMethod.Pickup && store) || Boolean(!isServiceStoreEnabled || shippingState.serviceStore || serviceStore?.isOptional),
    [isServiceStoreEnabled, serviceStore?.isOptional, shippingState.serviceStore, store]
  );
  const isShippingMethodValid = React.useCallback(
    (selectedShippingMethod) => {
      if (!selectedShippingMethod.method) return false;
      if (selectedShippingMethod.method == ShippingMethod.PickupPoint && !pickupPoint) return false;
      if (selectedShippingMethod.method == ShippingMethod.Pickup && !store) return false;
      return selectedShippingMethod.isValid;
    },
    [pickupPoint, store]
  );
  const areCustomerDetailsValid = React.useCallback(() => {
    if (formFieldsMap) {
      return Object.entries(formFieldsMap).every(([formName, formFieldItems]) => {
        if (!isCompanyOrder && formName == "company") return true;
        if (isCompanyOrder && formName == "invoice") return true;
        if (formName == "deliveryAddress" && isAddressesEqual) return true;
        if (formName == "deliveryAddress" && !userDetailsState[formName] && userDetailsState.billingAddress) {
          return true;
        }
        const isValid = isFormDataValid(formFieldItems, userDetailsState[formName]);
        return isValid;
      });
    } else {
      return false;
    }
  }, [isAddressesEqual, formFieldsMap, isCompanyOrder, userDetailsState]);
  const isConsentGiven = React.useCallback(
    (consents) => {
      const consentsValid = consentSettings?.implicitTermsAndConditions ? true : Boolean(consents && !consents.some((consent) => !consent.isChecked));
      if (!consentsValid) {
        updateConsents(
          consents.map((consent) => ({
            ...consent,
            shouldValidate: !!consent.required
          }))
        );
      }
      return consentsValid;
    },
    [consentSettings?.implicitTermsAndConditions, updateConsents]
  );
  const isOrderDetailsValid = React.useCallback(
    (selectedShippingMethod, consents) => {
      return isConsentGiven(consents || []) && isShippingMethodValid(selectedShippingMethod) && areCustomerDetailsValid() && isServiceStoreValid(selectedShippingMethod);
    },
    [areCustomerDetailsValid, isConsentGiven, isServiceStoreValid, isShippingMethodValid]
  );
  const { setCustomerDetails, updateError, isUpdating } = useSetCustomerDetails({
    ...props?.onComplete ? { onCompleted: () => props.onComplete() } : {},
    onError: () => {
      toast.failed("checkout.errors.form.general");
    }
  });
  const handleOrderDetailsValidation = React.useCallback(
    (selectedShippingMethod, consents) => {
      if (userDetailsState.billingAddress && isOrderDetailsValid(selectedShippingMethod, consents)) {
        setisOrderDetailsValid(true);
        return true;
      } else {
        return false;
      }
    },
    [isOrderDetailsValid, setisOrderDetailsValid, userDetailsState.billingAddress]
  );
  const saveCustomerInformationWithoutValidation = React.useCallback(async () => {
    const { billingAddress, deliveryAddress, contactDetails, invoice, company } = userDetailsState;
    const shippingDetails = {
      billingAddress,
      deliveryAddress,
      contactDetails,
      invoice,
      company
    };
    const customerDetaislPayload = getCustomerDetailsPayload({
      shippingDetails,
      opcoCountry: intl.country,
      locale: intl.locale,
      isMultilingualEnabled,
      selectedPickupPoint: shippingState.pickupPoint ?? void 0,
      selectedShippingMethod: shippingState.selectedShippingMethod.method,
      selectedStore: shippingState.store ?? void 0
    });
    return await setCustomerDetails(customerDetaislPayload);
  }, [
    intl.country,
    intl.locale,
    isMultilingualEnabled,
    setCustomerDetails,
    shippingState.pickupPoint,
    shippingState.selectedShippingMethod.method,
    shippingState.store,
    userDetailsState
  ]);
  const saveCustomerInformation = React.useCallback(
    async (selectedShippingMethod, consents) => {
      if (userDetailsState.billingAddress && isOrderDetailsValid(selectedShippingMethod, consents)) {
        setisOrderDetailsValid(true);
        const { billingAddress, deliveryAddress, contactDetails, invoice, company } = userDetailsState;
        const shippingDetails = {
          billingAddress,
          deliveryAddress,
          contactDetails,
          invoice,
          company
        };
        const customerDetaislPayload = getCustomerDetailsPayload({
          shippingDetails,
          opcoCountry: intl.country,
          locale: intl.locale,
          isMultilingualEnabled,
          selectedPickupPoint: shippingState.pickupPoint ?? void 0,
          selectedShippingMethod: shippingState.selectedShippingMethod.method,
          selectedStore: shippingState.store ?? void 0
        });
        return await setCustomerDetails(customerDetaislPayload);
      } else {
        setisOrderDetailsValid(false);
        setIsOrderInProgress(false);
        toast.failed("checkout.errors.form.general");
        throw Error("checkout.errors.form.general");
      }
    },
    [
      isOrderDetailsValid,
      intl.country,
      intl.locale,
      isMultilingualEnabled,
      setisOrderDetailsValid,
      setCustomerDetails,
      setIsOrderInProgress,
      shippingState.pickupPoint,
      shippingState.selectedShippingMethod,
      shippingState.store,
      userDetailsState
    ]
  );
  return React.useMemo(
    () => ({
      saveCustomerInformation,
      saveCustomerInformationWithoutValidation,
      handleOrderDetailsValidation,
      isUpdating,
      updateError: Boolean(updateError)
    }),
    [
      isUpdating,
      saveCustomerInformation,
      updateError,
      saveCustomerInformationWithoutValidation,
      handleOrderDetailsValidation
    ]
  );
};
export {
  useValidateAndSetOrderDetails
};
//# sourceMappingURL=useValidateAndSetOrderDetails.js.map
